/**
 * Centralized Test IDs for E2E Testing
 *
 * This file contains all test IDs used in Playwright tests.
 * Using constants ensures consistency and prevents typos.
 *
 * Naming Convention:
 * - Use kebab-case for test ID values
 * - Structure: {feature}-{component}-{element}
 * - Example: 'form-builder-add-field-button'
 */

export const TEST_IDS = {
  /**
   * Form Builder - Main form configuration interface
   */
  FORM_BUILDER: {
    ADD_FIELD_BUTTON: 'form-builder-add-field-button',
  },

  /**
   * Field Editor - Field configuration interface (used for all field types)
   */
  FIELD_EDITOR: {
    TYPE_SELECT: 'field-editor-type-select',
    LABEL_INPUT: 'field-editor-label-input',
    CREATE_BUTTON: 'field-editor-create-button',
    ACCESS_SELECT: 'field-editor-access-select',
  },

  /**
   * Dropdown Field - Dropdown field configuration
   */
  DROPDOWN_FIELD: {
    OPTIONS_INPUT: 'dropdown-field-options-input',
  },

  /**
   * Canvas Field - Canvas/infinite canvas field configuration
   */
  CANVAS_FIELD: {
    TEMPLATE_SELECT: 'canvas-field-template-select',
    CONFIGURE_BUTTON: 'canvas-field-configure-button',
    BUTTON_TEXT_INPUT: 'canvas-field-button-text-input',
  },

  /**
   * Form View - Form display and interaction interface
   */
  FORM_VIEW: {
    SAVE_BUTTON: 'form-view-save-button',
    STATUS_SELECT: 'form-view-status-select',
    FORM_CONTAINER: 'form-view-container',
  },

  /**
   * Comment Field - Text comment field in forms
   */
  COMMENT_FIELD: {
    INPUT: 'comment-field-input',
  },

  /**
   * Dropdown Field Preview - Dropdown field in form view
   */
  DROPDOWN_FIELD_PREVIEW: {
    SELECT: 'dropdown-field-preview-select',
  },
} as const;

/**
 * Type helper for test IDs
 * Usage: const testId: TestId = TEST_IDS.FORM_BUILDER.ADD_FIELD_BUTTON;
 */
export type TestId = (typeof TEST_IDS)[keyof typeof TEST_IDS][keyof (typeof TEST_IDS)[keyof typeof TEST_IDS]];
